import React, { useState, useEffect } from 'react';
import './Navbar.css';
import LoginModal from './LoginModal';
import RegistrationModal from './RegistrationModal';
import { AuthService } from '../services/auth.service';
import { IUser } from '../types/auth.types';
import minlogo from '../assets/min-logo.png';
import { SHOW_STAGE_RESULTS, isRegistrationOpen } from '../config/featureFlags';

// Используем новый логотип Кванториума по ссылке
const kvanLogoUrl = 'https://kvantorium24.ru/wp-content/uploads/2024/08/Logo_v_liniyu_3x.png';

const Navbar: React.FC = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isRegistrationModalOpen, setIsRegistrationModalOpen] = useState(false);
  const [isResultsOpen, setIsResultsOpen] = useState(false);
  const [isFinalResultsOpen, setIsFinalResultsOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<IUser | null>(null);

  useEffect(() => {
    // Проверяем, залогинен ли пользователь при загрузке
    const user = AuthService.getUser();
    console.log('Initial user check:', user);
    if (user) {
      setIsLoggedIn(true);
      setCurrentUser(user);
    }

    // Подписываемся на изменения статуса авторизации
    const unsubscribe = AuthService.subscribe((loggedIn) => {
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        setCurrentUser(AuthService.getUser());
      } else {
        setCurrentUser(null);
      }
    });

    // Отписываемся при размонтировании компонента
    return () => {
      unsubscribe();
    };
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleLogin = async () => {
    const user = AuthService.getUser();
    console.log('User after login:', user);
    if (user) {
      setIsLoggedIn(true);
      setCurrentUser(user);
    }
  };

  const handleLogout = async () => {
    console.log('Logout initiated');
    try {
      await AuthService.logout();
      console.log('Logout successful');
      // Здесь не нужно устанавливать состояние напрямую, так как это будет сделано через подписку
    } catch (error) {
      console.error('Logout error in component:', error);
      // Если произошла ошибка, все равно сбрасываем локальное состояние
      setIsLoggedIn(false);
      setCurrentUser(null);
    }
  };

  const showLoginModal = () => {
    setIsRegistrationModalOpen(false);
    setIsLoginModalOpen(true);
  };

  const showRegistrationModal = () => {
    if (!isRegistrationOpen()) {
      return;
    }
    setIsLoginModalOpen(false);
    setIsRegistrationModalOpen(true);
  };

  console.log('Render state - isLoggedIn:', isLoggedIn, 'currentUser:', currentUser);

  const registrationOpen = isRegistrationOpen();

  return (
    <>
      <nav className="navbar">
        <div className="navbar-container">
          <div className="navbar-links">
            <button 
              onClick={() => scrollToSection('about')} 
              className="nav-link"
            >
              О мероприятии
            </button>
            <button 
              onClick={() => scrollToSection('documents')} 
              className="nav-link"
            >
              Положение о конкурсе
            </button>
            {!isLoggedIn && registrationOpen && (
              <button
                onClick={showRegistrationModal}
                className="nav-link"
              >
                Регистрация
              </button>
            )}
            {SHOW_STAGE_RESULTS && (
            <>
            <div className="nav-dropdown">
              <button 
                onClick={() => setIsResultsOpen((prev) => !prev)}
                className="nav-link"
              >
                Результаты отборочного этапа
              </button>
              {isResultsOpen && (
                <div className="dropdown-menu">
                  <a 
                    className="dropdown-item"
                    href="/static/Protokol_Nanokhakaton_otborochny_etap.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Протокол отборочного этапа
                  </a>
                  <a 
                    className="dropdown-item"
                    href="https://disk.yandex.ru/d/fompHSA98-b9nw"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Сертификаты участников
                  </a>
                  <a 
                    className="dropdown-item"
                    href="https://disk.yandex.ru/d/1nptepTrHhKs8Q"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Письма-приглашения финалистов
                  </a>
                </div>
              )}
            </div>
            <div className="nav-dropdown">
              <button 
                onClick={() => setIsFinalResultsOpen((prev) => !prev)}
                className="nav-link"
              >
                Результаты очного этапа
              </button>
              {isFinalResultsOpen && (
                <div className="dropdown-menu">
                  <a 
                    className="dropdown-item"
                    href="/static/результаты очного этапа.pdf"
                    download="результаты очного этапа.pdf"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Рейтинговая таблица результатов
                  </a>
                </div>
              )}
            </div>
            </>
            )}
            <button 
              onClick={() => scrollToSection('contacts')} 
              className="nav-link"
            >
              Контакты
            </button>
          </div>
          <div className="right-side">
            <div className="navbar-auth">
              {!isLoggedIn ? (
                <button 
                  onClick={() => setIsLoginModalOpen(true)}
                  className="auth-button login-button"
                >
                  Вход
                </button>
              ) : (
                <div className="auth-container">
                  <span className="user-name">
                    {currentUser?.captainFullName}
                  </span>
                  <button 
                    onClick={handleLogout}
                    className="auth-button logout-button"
                  >
                    Выход
                  </button>
                </div>
              )}
            </div>
            <div className="navbar-logo">
              <img src={minlogo} alt="Минобрнауки России" className="min-logo" />
              <a href="https://vk.com/kvantorium24" target="_blank" rel="noopener noreferrer">
                <img src={kvanLogoUrl} alt="Кванториум" className="kvan-logo" />
              </a>
            </div>
          </div>
        </div>
      </nav>

      <LoginModal 
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onSuccess={handleLogin}
      />

      <RegistrationModal 
        isOpen={isRegistrationModalOpen}
        onClose={() => setIsRegistrationModalOpen(false)}
        onShowLogin={showLoginModal}
      />
    </>
  );
};

export default Navbar; 